import LayoutHR from "layouts/layout-hr";
import React, { useState, useMemo } from "react";
import { Dropdown } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import CreateTeamPage from "./components/create-team-page";
import {
  showDevNotice,
  // DEVELOPMENT_STATUS,
  getDevelopmentBanner,
} from "utils/devNotice";

const TeamPage = () => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  showDevNotice("Team Management Page");

  // Dummy data for team members
  const dummyTeamData = [
    {
      id: 1,
      name: "John Doe",
      nickname: "Johnny",
      username: "johndoe",
      email: "john@example.com",
      whatsapp: "+1234567890",
      role: "Manager",
      photo:
        "https://m.media-amazon.com/images/S/pv-target-images/16627900db04b76fae3b64266ca161511422059cd24062fb5d900971003a0b70._SX1080_FMjpg_.jpg",
      created_at: "2025-01-15",
    },
    {
      id: 2,
      name: "Jane Smith",
      nickname: "Janie",
      username: "janesmith",
      email: "jane@example.com",
      whatsapp: "+1234567891",
      role: "Developer",
      photo:
        "https://m.media-amazon.com/images/S/pv-target-images/16627900db04b76fae3b64266ca161511422059cd24062fb5d900971003a0b70._SX1080_FMjpg_.jpg",
      created_at: "2025-01-16",
    },
    {
      id: 3,
      name: "Bob Johnson",
      nickname: "Bobby",
      username: "bobjohnson",
      email: "bob@example.com",
      whatsapp: "+1234567892",
      role: "Designer",
      photo:
        "https://m.media-amazon.com/images/S/pv-target-images/16627900db04b76fae3b64266ca161511422059cd24062fb5d900971003a0b70._SX1080_FMjpg_.jpg",
      created_at: "2025-01-17",
    },
    {
      id: 4,
      name: "Alice Wilson",
      nickname: "Ali",
      username: "alicewilson",
      email: "alice@example.com",
      whatsapp: "+1234567893",
      role: "HR Specialist",
      photo:
        "https://m.media-amazon.com/images/S/pv-target-images/16627900db04b76fae3b64266ca161511422059cd24062fb5d900971003a0b70._SX1080_FMjpg_.jpg",
      created_at: "2025-01-18",
    },
    {
      id: 5,
      name: "Charlie Brown",
      nickname: "Chuck",
      username: "charliebrown",
      email: "charlie@example.com",
      whatsapp: "+1234567894",
      role: "Accountant",
      photo:
        "https://m.media-amazon.com/images/S/pv-target-images/16627900db04b76fae3b64266ca161511422059cd24062fb5d900971003a0b70._SX1080_FMjpg_.jpg",
      created_at: "2025-01-19",
    },
  ];

  // Filter and paginate data
  const filteredData = useMemo(() => {
    const filtered = dummyTeamData.filter(
      (item) =>
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.email.toLowerCase().includes(search.toLowerCase()) ||
        item.role.toLowerCase().includes(search.toLowerCase())
    );

    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;

    return {
      data: filtered.slice(startIndex, endIndex),
      pagination: {
        current_page: currentPage,
        last_page: Math.ceil(filtered.length / perPage),
        per_page: perPage,
        total: filtered.length,
        from: startIndex + 1,
        to: Math.min(endIndex, filtered.length),
        prev_page_url: currentPage > 1 ? true : null,
        next_page_url: endIndex < filtered.length ? true : null,
      },
    };
  }, [search, currentPage, perPage]);

  const teamData = filteredData;
  const loading = false;
  const error = null;

  const {
    // handleSuccess,
    handleError,
    showCanvasHandler,
    handleSuccessToast,
    // handleLoadingThenSuccessToast,
  } = useCanvasHandler();

  const handleCreate = (e) => {
    e.preventDefault();
    showCanvasHandler(
      "Create Team Member",
      <CreateTeamPage
        onSuccess={() => {
          handleSuccessToast(
            true,
            "Team member created successfully (Demo Mode)"
          );
        }}
        onError={handleError}
      />
    );
  };

  const handleEdit = (e, data) => {
    e.preventDefault();
    showCanvasHandler(
      "Update Team Member",
      <CreateTeamPage
        initialData={data}
        onSuccess={() => {
          handleSuccessToast(
            true,
            "Team member updated successfully (Demo Mode)"
          );
        }}
        onError={handleError}
      />
    );
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      handleSuccessToast(true, "Team member deleted successfully (Demo Mode)");
    }
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setSearch(e.target.value);
      setCurrentPage(1);
    }
  };

  if (loading)
    return (
      <LayoutHR>
        <div
          className="container-xl d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </LayoutHR>
    );

  if (error)
    return (
      <LayoutHR>
        <div className="container-xl">
          <div className="alert alert-danger">
            Error loading team data: {error.message}
          </div>
        </div>
      </LayoutHR>
    );

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-xl">
        {/* Page Header */}
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">Team Management</h2>
              <div className="text-muted mt-1">
                Manage team members and their information
              </div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleCreate}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-plus"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Team Member
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="input-icon">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search team members..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearch}
                  />
                  <span className="input-icon-addon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon icon-tabler icon-tabler-search"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <circle cx="10" cy="10" r="7" />
                      <line x1="21" y1="21" x2="15" y2="15" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Action</th>
                    <th>Photo</th>
                    <th>Name</th>
                    <th>Nickname</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Whatsapp</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {teamData?.data?.map((member, index) => (
                    <tr key={member.id}>
                      <td>{(currentPage - 1) * 10 + index + 1}</td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant="default"
                            size="sm"
                            id={`dropdown-${member.id}`}
                          >
                            Actions
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={(e) => handleEdit(e, member)}
                            >
                              Edit
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleDelete(member.id)}
                              className="text-danger"
                            >
                              Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                      <td>
                        {/* <div className="avatar avatar-sm">
                          <img
                            src={member.photo || "/default-avatar.png"}
                            alt={member.name}
                            className="avatar-img"
                          />
                        </div> */}
                        <span
                          className="avatar"
                          style={{
                            backgroundImage: `url(${
                              member.photo || "/default-avatar.png"
                            })`,
                          }}
                        ></span>
                      </td>
                      <td>{member.name}</td>
                      <td>{member.nickname}</td>
                      <td>{member.username}</td>
                      <td>{member.email}</td>
                      <td>{member.whatsapp}</td>
                      <td>
                        <span className="badge bg-primary text-white">
                          {member.role}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {teamData?.pagination && (
              <div className="card-footer d-flex align-items-center">
                <div className="d-flex align-items-center me-3">
                  <span className="text-muted me-2">Show:</span>
                  <select
                    className="form-select form-select-sm"
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing perPage
                    }}
                    style={{ width: "auto" }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-muted ms-2">entries</span>
                </div>
                <p className="m-0 text-muted">
                  Showing <span>{teamData.pagination.from || 0}</span> to{" "}
                  <span>{teamData.pagination.to || 0}</span> of{" "}
                  <span>{teamData.pagination.total || 0}</span> entries
                </p>
                <ul className="pagination m-0 ms-auto">
                  <li
                    className={`page-item ${
                      !teamData.pagination.prev_page_url ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={!teamData.pagination.prev_page_url}
                    >
                      Previous
                    </button>
                  </li>
                  {Array.from(
                    { length: teamData.pagination.last_page || 1 },
                    (_, i) => i + 1
                  ).map((page) => (
                    <li
                      key={page}
                      className={`page-item ${
                        page === currentPage ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      !teamData.pagination.next_page_url ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!teamData.pagination.next_page_url}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutHR>
  );
};

export default TeamPage;
